import { AuthenticationManager } from "@playit/shared-authentication";
import axios from "axios";
import { AnimationDetailsDTO } from "../dto/AnimationDetailsDTO";
import { AnimationDTO } from "../dto/AnimationDTO";
import { AnimationPreviewDTO } from "../dto/AnimationPreviewDTO";
import { AnimationsPagedQueryParametersDTO } from "../dto/AnimationsPagedQueryParametersDTO";

/**
 * Result of a single-animation lookup. `notFound` is the API's answer for an
 * `externalId` it does not know (or a route segment that is not a GUID), which
 * callers show as "animation unavailable" and must not retry. `error` covers
 * everything else (network, 5xx) and may be retried.
 */
export type AnimationLookupResult<T> =
  | { status: "ok"; data: T }
  | { status: "notFound" }
  | { status: "error" };

function isNotFoundError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | undefined)
    ?.response?.status;
  return status === 404;
}

export class AnimationService {
  private static readonly baseUrl = `${process.env.REACT_APP_PLAYIT_CREATORAPI}`;

  private static getAuthConfig() {
    const token = AuthenticationManager.GetToken();

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }

  private static getPagedConfig(
    page: number,
    pageSize: number,
    queryParams?: AnimationsPagedQueryParametersDTO
  ) {
    return {
      ...this.getAuthConfig(),
      params: {
        PageSize: pageSize,
        PageNumber: page,
        Name: queryParams?.name,
      },
    };
  }

  private static getTotalCount(headers: Record<string, unknown> | undefined) {
    const paginationHeader = headers?.["x-pagination"];
    if (typeof paginationHeader !== "string") {
      throw new Error("Missing x-pagination header");
    }

    return JSON.parse(paginationHeader)["totalCount"];
  }

  private static async lookup<T>(
    path: string
  ): Promise<AnimationLookupResult<T>> {
    try {
      const { data, status } = await axios.get<T>(path, this.getAuthConfig());
      return status === 200 ? { status: "ok", data } : { status: "error" };
    } catch (error) {
      if (isNotFoundError(error)) return { status: "notFound" };
      console.error(error);
      return { status: "error" };
    }
  }

  static async GetAnimations(
    orgId: string,
    page: number,
    pageSize: number,
    queryParams?: AnimationsPagedQueryParametersDTO
  ) {
    const path = `${this.baseUrl}/organizations/${orgId}/animations`;
    const config = this.getPagedConfig(page, pageSize, queryParams);

    try {
      const { data, headers } = await axios.get<AnimationDTO[]>(path, config);
      const totalCount = this.getTotalCount(headers);

      return {
        animations: data ?? [],
        totalCount,
      };
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  /** Looks up one animation by its `externalId` GUID (never by name). */
  static GetAnimationDetails(orgId: string, externalId: string) {
    return this.lookup<AnimationDetailsDTO>(
      `${this.baseUrl}/organizations/${orgId}/animations/${externalId}`
    );
  }

  /** Looks up an animation's preview gif by its `externalId` GUID. */
  static GetAnimationPreview(orgId: string, externalId: string) {
    return this.lookup<AnimationPreviewDTO>(
      `${this.baseUrl}/organizations/${orgId}/animations/${externalId}/preview`
    );
  }
}
